import {
  ITranslationMap,
  type ICustomMappingSourceOption,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  IManualTransactionType,
  TransactionProvider,
} from '@principle-theorem/principle-core/interfaces';
import {
  INamedDocument,
  isINamedDocument,
  isSameRef,
  type AtLeast,
  type IXSLXExport,
  type XSLXCell,
} from '@principle-theorem/shared';
import type { Column } from 'exceljs';

export interface ITransactionTypeXSLX {
  sourceId: string;
  sourceName: string;
  mapTo: string;
}

export interface IBaseTransactionTypeOption {
  label: string;
  destinationValue: { value: TransactionProvider };
  associatedValue?: INamedDocument<IManualTransactionType>;
}

export const TRANSACTION_TYPE_HEADERS: AtLeast<Column, 'key' | 'header'>[] = [
  {
    key: 'sourceId',
    header: 'Id',
  },
  {
    key: 'sourceName',
    header: 'Payment Type',
    width: 45,
  },
  {
    key: 'mapTo',
    header: 'Map To',
    width: 30,
  },
];

export class TransactionTypeToXSLX
  implements IXSLXExport<ICustomMappingSourceOption, ITransactionTypeXSLX>
{
  headers = TRANSACTION_TYPE_HEADERS;

  constructor(
    private _transactionTypeOptions: IBaseTransactionTypeOption[],
    private _existingMappings: ITranslationMap<
      IManualTransactionType,
      TransactionProvider
    >[]
  ) {}

  translate(
    records: ICustomMappingSourceOption[]
  ): Record<keyof ITransactionTypeXSLX, XSLXCell>[] {
    return records.map((record) => {
      const existingRecord = this._existingMappings.find(
        (existingMapping) => existingMapping.sourceLabel === record.label
      );

      const destinationValue = existingRecord?.destinationValue;
      const mappedValue = destinationValue
        ? this._transactionTypeOptions.find((medicalHistoryOption) => {
            const isDestinationValue =
              medicalHistoryOption.destinationValue.value ===
              existingRecord.destinationValue?.value;

            if (!existingRecord.associatedValue) {
              return isDestinationValue;
            }

            const isAssociatedValue =
              isINamedDocument(existingRecord.associatedValue) &&
              isSameRef(
                medicalHistoryOption.associatedValue,
                existingRecord.associatedValue
              );

            return isDestinationValue && isAssociatedValue;
          })
        : undefined;

      return {
        sourceId: {
          value: record.value,
        },
        sourceName: {
          value: record.label,
        },
        mapTo: {
          value: mappedValue?.label,
          dataValidation: {
            type: 'list',
            formulae: [
              `"${this._transactionTypeOptions
                .map((transactionTypeOption) => transactionTypeOption.label)
                .join(',')}"`,
            ],
          },
        },
      };
    });
  }
}
